import React from "react";

/** Isometric “before” — scattered one-off screens */
export function IsoBeforeChaos() {
  return (
    <svg
      className="collabstr-iso"
      viewBox="0 0 560 320"
      role="img"
      aria-label="One-off screens with no shared system"
    >
      <defs>
        <filter id="iso-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000" floodOpacity="0.08" />
        </filter>
      </defs>
      <rect width="560" height="320" fill="#efefef" rx="12" />
      {[
        { x: 48, y: 140, w: 120, h: 72, rot: -8 },
        { x: 200, y: 52, w: 128, h: 76, rot: 6 },
        { x: 320, y: 128, w: 118, h: 70, rot: -4 },
        { x: 140, y: 200, w: 132, h: 78, rot: 10 },
        { x: 380, y: 188, w: 124, h: 74, rot: -12 },
      ].map((p, i) => (
        <g key={i} transform={`translate(${p.x + p.w / 2}, ${p.y + p.h / 2}) rotate(${p.rot})`} filter="url(#iso-shadow)">
          <rect x={-p.w / 2} y={-p.h / 2} width={p.w} height={p.h} rx="8" fill="#fff" stroke="#d8d8d8" />
          <rect x={-p.w / 2 + 10} y={-p.h / 2 + 10} width={p.w - 20} height={8} rx="2" fill="#e8e8e8" />
          <rect x={-p.w / 2 + 10} y={-p.h / 2 + 26} width={(p.w - 20) * 0.7} height={6} rx="2" fill="#f0f0f0" />
        </g>
      ))}
    </svg>
  );
}

/** Isometric campaign metric — 56 min → 9 min */
export function IsoCampaignProof() {
  return (
    <svg className="collabstr-iso" viewBox="0 0 560 280" role="img" aria-label="Campaign flow: 56 minutes to 9 minutes">
      <defs>
        <filter id="iso-shadow-c" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#000" floodOpacity="0.07" />
        </filter>
        <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="#999" />
        </marker>
      </defs>
      <rect width="560" height="280" fill="#efefef" rx="12" />
      <g transform="skewY(-12) translate(0, 24)">
        <g transform="translate(72, 48)" filter="url(#iso-shadow-c)">
          <rect width="160" height="120" rx="10" fill="#fff" stroke="#ddd" />
          <text x="80" y="44" textAnchor="middle" fill="#888" fontSize="11" fontFamily="ui-monospace, monospace">
            BEFORE
          </text>
          <text x="80" y="78" textAnchor="middle" fill="#333" fontSize="28" fontWeight="600" fontFamily="system-ui">
            56
          </text>
          <text x="80" y="98" textAnchor="middle" fill="#666" fontSize="12" fontFamily="system-ui">
            min · 20+ steps
          </text>
        </g>
        <g transform="translate(328, 72)" filter="url(#iso-shadow-c)">
          <rect width="160" height="120" rx="10" fill="#fff" stroke="#c8c8c8" />
          <text x="80" y="44" textAnchor="middle" fill="#888" fontSize="11" fontFamily="ui-monospace, monospace">
            AFTER
          </text>
          <text x="80" y="78" textAnchor="middle" fill="#111" fontSize="28" fontWeight="600" fontFamily="system-ui">
            9
          </text>
          <text x="80" y="98" textAnchor="middle" fill="#666" fontSize="12" fontFamily="system-ui">
            min · 2 clicks
          </text>
        </g>
      </g>
      <path d="M248 140 L288 140" stroke="#999" strokeWidth="2" markerEnd="url(#arrow)" />
    </svg>
  );
}

/** Isometric SOP loop card */
export function IsoContributionLoop() {
  const steps = ["01 PROPOSE", "02 REVIEW", "03 MERGE", "04 ANNOUNCE"];
  return (
    <svg className="collabstr-iso" viewBox="0 0 560 300" role="img" aria-label="Contribution loop: propose, review, merge, announce">
      <rect width="560" height="300" fill="#efefef" rx="12" />
      <g transform="translate(140, 40) skewY(-10)">
        <rect width="280" height="200" rx="12" fill="#fff" fillOpacity="0.92" stroke="#e0e0e0" filter="url(#iso-s)" />
        <text x="24" y="36" fill="#666" fontSize="13" fontFamily="ui-monospace, monospace">
          SOPs
        </text>
        {steps.map((s, i) => (
          <text
            key={s}
            x="24"
            y={72 + i * 36}
            fill="#444"
            fontSize="15"
            fontWeight="500"
            fontFamily="system-ui, sans-serif"
            letterSpacing="0.04em"
          >
            {s}
          </text>
        ))}
      </g>
      <defs>
        <filter id="iso-s" x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#000" floodOpacity="0.09" />
        </filter>
      </defs>
    </svg>
  );
}

export function IsoSystemStackImage() {
  return (
    <img
      src="/collabstr-illustrations/collabstr-iso-system-stack.png"
      alt="Isometric stack: tokens, components, patterns, SOPs, and design documentation"
      className="collabstr-iso collabstr-iso--photo"
      loading="lazy"
    />
  );
}
